import { AddressDetail, GeometryCoordinates, NswPointAddressDetails, NswPointRawAddress } from '../types';

const DEFAULT_DATASET = 'GNAF';

const hasValue = (value: unknown): boolean => value != null && String(value).trim() !== '';

const toNumberOrNull = (value: number | string | null): number | null => {
  if (!hasValue(value)) {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const toStringOrNull = (value: number | string | null): string | null => {
  if (toNumberOrNull(value) == null) {
    return null;
  }

  return String(value).trim();
};

const toStreetNumbers = (
  houseNumber: string | null
): Pick<NswPointAddressDetails, 'streetNumber1' | 'streetNumber2'> => {
  if (!houseNumber) {
    return {
      streetNumber1: null,
      streetNumber2: null,
    };
  }

  const [streetNumber1, streetNumber2 = null] = houseNumber.split('-').map((streetNumber) => streetNumber.trim());

  return {
    streetNumber1,
    streetNumber2,
  };
};

const toCoordinates = (address: AddressDetail): GeometryCoordinates | null => {
  const longitude = toNumberOrNull(address.longitude);
  const latitude = toNumberOrNull(address.latitude);

  if (longitude == null || latitude == null) {
    return null;
  }

  return {
    coordinates: [longitude, latitude],
    type: 'Point',
  };
};

/**
 * The SNSW AutoSuggestAddress component still runs its own NSW Point converter over custom backend responses.
 * The v3 address API returns a flat `address` payload and sets `rawAddress` to null, so this mapper rebuilds the
 * raw NSW Point wrapper shape expected by that converter.
 */
export const toNswPointRawAddress = (address: AddressDetail, requestTimestamp = Date.now()): NswPointRawAddress => {
  const streetNumbers = toStreetNumbers(address.houseNumber ?? address.buildingNumber);
  const coordinates = toCoordinates(address);
  const isNotFound = !Boolean(address.uniqueId) && coordinates == null;

  return {
    data: {
      addressId: address.uniqueId,
      addressRecordType: null,
      dbType: null,
      dataset: isNotFound ? null : DEFAULT_DATASET,
      properties: {
        pafVersion: null,
        pafExpiryDate: '',
        matchResult: isNotFound ? 'none' : address.matchQuality,
        matchQualityPercentage: address.matchQualityPercentage,
        matchQualityPercentageDescription: address.matchQualityDescription,
        matchCode: null,
        addressIdentifier: null,
        geoFeature: null,
        primarySecondary: null,
        geoType: null,
        geoDatumCode: address.geoDatumCode,
        matchCertainty: isNotFound ? 'none' : address.matchCertainty,
      },
      addressDetails: {
        deliveryPointIdentifier: toStringOrNull(address.dPID),
        cadastralIdentifier: address.cadastralIdentifier,
        complexUnitIdentifier: address.unitNumber,
        complexLevelIdentifier: null,
        complexUnitType: address.unitType,
        complexLevelNumber: address.complexLevelNumber,
        complexLevelType: address.complexLevelType,
        formattedAddress: address.partialAddress ?? address.addressLine1,
        localityName: address.suburb,
        lotIdentifier: address.lotNumber,
        postcode: address.postcode,
        stateTerritory: address.state,
        streetName: address.streetName,
        streetNumber1: streetNumbers.streetNumber1,
        streetNumber2: streetNumbers.streetNumber2,
        streetType: address.streetType,
        postalDeliveryNumber: address.postalDeliveryNumber,
        postalDeliveryType: address.postalDeliveryType ?? address.postalType,
        siteName: address.buildingName ?? address.propertyName,
      },
      geo: coordinates
        ? {
            geoDatumCode: address.geoDatumCode,
            geoFeature: null,
            geometry: coordinates,
          }
        : null,
      asgsMain: {
        Y2016: {
          mbId: null,
          sa1Id: address.sA1Code,
          sa2Id: address.sA2Code,
          sa3Id: address.sA3Code,
          sa4Id: address.sA4Code,
        },
      },
      commonwealthElectorate: {
        commElectoralName: address.commonwealthElectorateName,
        commElectoralPid: address.commonwealthElectoratePID,
      },
      localGovernmentArea: {
        lgaName: address.lGAName,
        lgaShortName: address.lGAShortName,
        lgaPid: address.lGAID,
      },
      stateElectorate: [
        {
          stateElectoralName: address.stateElectorateName,
          stateElectoralType: address.stateElectorateType,
        },
      ],
    },
    requestTimestamp,
    requestDate: null,
  };
};
